<template>
   <header class="site-header">
        <div class="inner">
            <div class="brand">
                <a href="#" class="brand-logo" @click.prevent="exitAppRuntime">
                    <img src="/img/logo-sfz.png" />
                    <!-- <span class="badge">B</span>
                    <span class="title">
                        {{ t('pages.main.header.brandName') }}
                        <span class="subtitle">{{ t('pages.main.header.brandSubtitle') }}</span>
                    </span> -->
                </a>

                <div class="divider"></div>

                <Select
                    class="noerror inline icon-only"
                    :optionList="locale.languages"
                    :value="locale.locale"
                    :clearable="false"
                    :leftAuto="locale.rtl"
                    :rightAuto="!locale.rtl"
                    @change="onLanguageChange"
                />

                <div class="divider"></div>
                
                <a href="https://bilbomusic.com" target="_blank" rel="noopener noreferrer" class="external-link">
                    <span>{{ t('pages.main.header.externalLink') }}</span>
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                </a>
            </div>

            <div class="actions" style="display: none;">
                <div class="auth-nav">
                    <div v-if="!user.token">
                        <button id="open-signin-modal-btn" class="sign-in-btn" @click="openAuthModal">
                            {{ t('pages.main.header.signIn') }}
                        </button>
                    </div>
                    <div v-else>
                        <button v-if="appPlugin.profile?.id" id="user-avatar-btn"
                            class="avatar-btn"
                            :aria-label="t('pages.main.header.currentIdentity')"
                            @click="toggleContextMenu">

                            <Img v-if="appPlugin.profile.img?.resized || defaultUserImage?.resized"
                                :preview="appPlugin.profile.img?.resized || defaultUserImage?.resized"
                                :original="appPlugin.profile.img?.original || defaultUserImage?.original"
                                :alt="appPlugin.profile?.name"
                                class="avatar-img"
                            />

                            <span v-if="appPlugin.profile.img.type === 'artist'"
                                id="nav-artist-badge"
                                class="artist-badge"
                                :title="t('pages.main.header.artistBadgeTitle')"></span>
                        </button>

                        <div v-if="isContextMenuOpen" id="context-menu" class="context-menu">
                            <div class="header">
                                <p class="kicker">{{ t('pages.main.header.currentIdentity') }}</p>
                                <p class="name">{{ appPlugin.profile?.name }}</p>
                                <p class="role" v-if="appPlugin.profile?.type === 'artist'">{{ 'Artist' }}</p>
                            </div>
                            <div class="list">
                                <button v-for="item in appPlugin.profiles"
                                    :key="item.id"
                                    class="profile-switch-btn"
                                    :class="{ active: item.id === appPlugin.activeProfileId }"
                                    @click="appPlugin.setActiveProfile(item.id); toggleContextMenu()"
                                >
                                    <div class="avatar-wrap">
                                        <Img v-if="item.img?.resized || defaultUserImage?.resized"
                                            :preview="item.img?.resized || defaultUserImage?.resized"
                                            :original="item.img?.original || defaultUserImage?.original"
                                            :alt="item?.name"
                                            class="avatar"
                                        />

                                        <span v-if="item.type === 'artist'" class="badge"></span>
                                    </div>
                                    <div class="info">
                                        <p class="name">{{ item.name }}</p>
                                        <p class="sub" v-if="item.type === 'artist'">{{ 'Artist' }}</p>
                                    </div>
                                    <span v-if="item.id === appPlugin.activeProfileId" class="check">✓</span>
                                </button>
                            </div>
                            <div class="footer">
                                <button id="sign-out-toggle-btn" class="action-btn danger" @click="signOut">
                                    {{ t('pages.main.header.signOut') }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </header>
</template>

<script setup>
    import { computed } from 'vue';
    import { useI18n } from 'vue-i18n';

    import Img from '🔗/components/ui/Img.vue';
    import Select from '🔗/components/ui/Select.vue';

    const modals = stores.modals();
    const user = stores.user();
    const appPlugin = stores.appPlugin();
    const locale = stores.locale();

    const { t, tm } = useI18n();

    const defaultUserImage = computed(() => {
        return api.users().defaultUserImage;
    });

    const isContextMenuOpen = ref(false);

    const onLanguageChange = (payload) => {
        locale.updateLocale(payload.value);
    };

    const toggleContextMenu = (e) => {
        if (e) {
            e.stopPropagation();
        }
        isContextMenuOpen.value = !isContextMenuOpen.value;
    }

    const signOut = () => {
        toggleContextMenu();
        user.logout();
        appPlugin.syncProfiles();
    }

    const openAuthModal = () => {
        modals.open('auth');
    }
</script>

<style lang="scss" scoped>
@use '@/assets/scss/variables.scss' as *;

.site-header {
    position: sticky;
    top: 0;
    z-index: 40;
    @include bapps.glass-panel(rgba(248, 249, 250, 0.8), 20px, $color-border-subtle);
    border-top: none;
    border-left: none;
    border-right: none;
    @include bapps.transition-smooth;

    .inner {
        max-width: 72rem;
        margin: 0 auto;
        padding: 0 1.5rem;
        height: 5rem;
        @include bapps.flex-between;

        @include bapps.respond-to("sm") {
            padding: 0 2rem;
        }
        @include bapps.respond-to("lg") {
            padding: 0 3rem;
        }
    }

    .brand {
        @include bapps.flex-align-center;
        gap: 1.5rem;

        .divider {
            display: none;
            width: 1px;
            height: 0.875rem;
            background-color: rgba(0, 0, 0, 0.1);

            @include bapps.respond-to("sm") {
                display: block;
            }
        }

        .external-link {
            display: none;
            font-size: 0.8125rem;
            font-weight: 500;
            color: $color-subtle;
            @include bapps.transition-smooth(color);
            @include bapps.flex-align-center;
            gap: 0.25rem;

            svg {
                width: 0.75rem;
                height: 0.75rem;
                opacity: 0.6;
            }

            &:hover {
                color: $color-charcoal;
            }

            @include bapps.respond-to("sm") {
                display: inline-flex;
            }
        }
    }

    .actions {
        @include bapps.flex-align-center;
        gap: 1rem;
    }
}

// Brand Logo
.brand-logo {
    @include bapps.flex-align-center;
    gap: 0.75rem;

    > img {
        height: 60px;
    }

    .badge {
        width: 2rem;
        height: 2rem;
        border-radius: 0.75rem;
        background-color: $color-charcoal;
        color: #ffffff;
        @include bapps.flex-center;
        font-family: $font-display;
        font-weight: 900;
        font-size: 0.875rem;
        letter-spacing: -0.05em;
        @include bapps.transition-smooth(transform);
    }

    &:hover .badge {
        transform: scale(0.95);
    }

    .title {
        font-family: $font-display;
        font-weight: 900;
        font-size: 1.25rem;
        letter-spacing: -0.02em;
        color: $color-charcoal;
        display: flex;
        align-items: baseline;
        gap: 0.25rem;
    }

    .subtitle {
        font-family: $font-sans;
        font-weight: 500;
        font-size: 0.75rem;
        letter-spacing: normal;
        color: $color-subtle;
    }
}

// Language Switcher Dropdown
.lang-switcher {
    position: relative;

    .toggle-btn {
        @include bapps.button-reset;
        font-size: 0.75rem;
        font-weight: 700;
        color: $color-subtle;
        padding: 0.375rem 0.625rem;
        border-radius: 0.5rem;
        @include bapps.flex-align-center;
        gap: 0.25rem;
        @include bapps.transition-smooth(color);

        svg {
            width: 0.75rem;
            height: 0.75rem;
            opacity: 0.5;
        }

        &:hover {
            color: $color-charcoal;
        }
    }

    .menu {
        position: absolute;
        right: 0;
        top: 100%;
        margin-top: 0.5rem;
        width: 7rem;
        background-color: #ffffff;
        border-radius: 1rem;
        box-shadow: $shadow-soft;
        border: 1px solid $color-border-light;
        padding: 0.375rem;
        z-index: 50;
        font-size: 0.75rem;
        font-weight: 600;
    }

    .option {
        @include bapps.button-reset;
        width: 100%;
        text-align: left;
        padding: 0.5rem 0.75rem;
        border-radius: 0.75rem;
        @include bapps.flex-between;
        color: $color-subtle;
        @include bapps.transition-smooth(background-color color);

        &:hover {
            background-color: rgba(0, 0, 0, 0.03);
        }

        &.active {
            color: $color-charcoal;
        }
    }
}

// Auth Header Navigation
.auth-nav {
    position: relative;

    .sign-in-btn {
        @include bapps.button-reset;
        padding: 0.5rem 1rem;
        font-size: 0.75rem;
        font-weight: 700;
        letter-spacing: -0.01em;
        border-radius: 9999px;
        background-color: $color-charcoal;
        color: #ffffff;
        box-shadow: $shadow-xs;
        @include bapps.transition-smooth;

        &:hover {
            background-color: $color-charcoal-hover;
        }

        &:active {
            transform: scale(0.95);
        }
    }

    .avatar-btn {
        @include bapps.button-reset;
        position: relative;
        padding: 2px;
        border-radius: 9999px;
        @include bapps.transition-smooth(box-shadow);

        &:hover {
            box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.1);
        }
    }

    .avatar-img {
        width: 2rem;
        height: 2rem;
        border-radius: 9999px;
        object-fit: cover;
        box-shadow: $shadow-xs;
    }

    .artist-badge {
        position: absolute;
        bottom: -2px;
        right: -2px;
        width: 0.75rem;
        height: 0.75rem;
        border-radius: 9999px;
        background-color: $color-bilbogreen;
        box-shadow: 0 0 0 2px $color-canvas;
    }
}

// User Context Dropdown Menu
.context-menu {
    position: absolute;
    right: 0;
    top: 100%;
    margin-top: 0.75rem;
    width: 16rem;
    background-color: #ffffff;
    border-radius: 1.5rem;
    box-shadow: $shadow-soft;
    border: 1px solid $color-border-light;
    padding: 0.5rem;
    z-index: 50;

    .header {
        padding: 0.75rem 1rem;
        border-bottom: 1px solid $color-border-subtle;
    }

    .kicker {
        @include bapps.kicker-text;
    }

    .name {
        font-family: $font-display;
        font-weight: 700;
        font-size: 0.875rem;
        color: $color-charcoal;
        margin-top: 0.25rem;
        @include bapps.text-truncate;
    }

    .role {
        font-size: 0.75rem;
        color: $color-subtle;
        @include bapps.text-truncate;
    }

    .list {
        padding: 0.5rem 0;
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
    }

    .footer {
        padding: 0.5rem 0.5rem 0.25rem;
        border-top: 1px solid $color-border-subtle;
        @include bapps.flex-between;
    }

    .action-btn {
        @include bapps.button-reset;
        font-size: 0.75rem;
        font-weight: 600;
        color: $color-subtle;
        padding: 0.375rem 0.5rem;
        border-radius: 0.5rem;
        @include bapps.transition-smooth(color);

        &:hover {
            color: $color-charcoal;
        }

        &.danger:hover {
            color: #dc2626;
        }

        &.compact {
            font-size: 0.6875rem;
        }
    }
}

// Profile Switcher Items
.profile-switch-btn {
    @include bapps.button-reset;
    width: 100%;
    text-align: left;
    padding: 0.5rem 0.75rem;
    border-radius: 1rem;
    @include bapps.flex-align-center;
    gap: 0.75rem;
    @include bapps.transition-smooth(background-color);

    &:hover {
        background-color: rgba(0, 0, 0, 0.02);
    }

    &.active {
        background-color: rgba(0, 0, 0, 0.04);
    }

    .avatar-wrap {
        position: relative;
        width: 1.75rem;
        height: 1.75rem;
        flex-shrink: 0;
    }

    .avatar {
        width: 100%;
        height: 100%;
        border-radius: 9999px;
        object-fit: cover;
    }

    .badge {
        position: absolute;
        bottom: -2px;
        right: -2px;
        width: 0.5rem;
        height: 0.5rem;
        border-radius: 9999px;
        background-color: $color-bilbogreen;
        box-shadow: 0 0 0 1px #ffffff;
    }

    .info {
        flex: 1;
        min-width: 0;
    }

    .name {
        font-size: 0.75rem;
        font-weight: 700;
        color: $color-charcoal;
        @include bapps.text-truncate;
    }

    .sub {
        font-size: 0.625rem;
        color: $color-subtle;
        @include bapps.text-truncate;
    }

    .check {
        font-size: 0.75rem;
        font-weight: 700;
        color: $color-charcoal;
    }
}
</style>
